import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import moment from 'moment'

import { Badge, Button, Sidebar } from '@hubii/ds-hubii'

import { useAuth, useAuthStore } from '@stores/auth.context/auth.context'
import { NotificationTest } from '@components/NotificationTest'

import NotificationSidebar from './components/notification-sidebar'
import StatusBar from './components/status-bar'

import { data } from './data'

import Chatwoot from '@components/ChatWoot'
import { getTermStatus } from '@services/hubii/hub-service'
import { IconNavigationMenu, IconReceiptSlip } from '@hubii/icons'
import Drawer from '@components/Drawer'
import { useIsMobile } from '@utils/hooks/useIsMobile'
import { useOrdersProposals } from '@stores/orders-proposal.context/orders-proposal.context'

interface IOptions {
  value: string
  label: string
}

const DrawerLayout = () => {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const { ordersProposal } = useOrdersProposals()

  const { user, selectedHub, setSelectedHub } = useAuthStore()

  const { logout } = useAuth()

  const [showStatusBar, toggleStatusBar] = useState(false)

  const [showNavSideBar, toggleNavSideBar] = useState(false)
  const [showNotificationSidebar, toggleNotificationSidebar] = useState(false)
  const [showNotificationTest, toggleNotificationTest] = useState(false)

  const [colorStatusBar, setColorStatusBar] = useState<'warning' | 'error' | 'success' | 'info'>(
    'warning',
  )
  const [messageStatusBar, setMessageStatusBar] = useState<string>('')

  const formatDate = (date: Date): string => {
    return moment(date).format('DD [de] MMMM')
  }

  const getStatusMessage = (
    userRole: 'owner' | 'employee' | 'support',
    dueDate: Date,
  ): { status: 'warning' | 'error' | 'success' | 'info'; text: string } => {
    const today = new Date()
    const dueDateObj = new Date(dueDate)

    const timeDiff = dueDateObj.getTime() - today.getTime()
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

    if (daysDiff >= 7) {
      return { status: 'warning', text: data.terms[userRole].warning(formatDate(dueDate)) }
    } else if (daysDiff <= 6 && daysDiff >= 1) {
      return { status: 'error', text: data.terms[userRole].last_days(formatDate(dueDate)) }
    } else if (daysDiff === 0) {
      return { status: 'error', text: data.terms[userRole].last_day }
    } else {
      return { status: 'error', text: data.terms[userRole].last_warning }
    }
  }

  const hasTermPending = async () => {
    try {
      const result = await getTermStatus(selectedHub?.uuid ?? '')

      localStorage.setItem('term_uuid', result.uuid ?? '')

      if (result) {
        toggleStatusBar(true)

        const { status, text } = getStatusMessage(
          selectedHub?.pivot?.user_role.name as 'owner' | 'employee' | 'support',
          new Date(result.due_date),
        )
        setColorStatusBar(status)
        setMessageStatusBar(text)
      } else {
        toggleStatusBar(false)
      }
    } catch (error) {
      if (error === 'Term Hub not found.') {
        toggleStatusBar(false)
        return
      } else {
        toggleStatusBar(false)
      }
    }
  }

  if (import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN) {
    Chatwoot()
  }

  const onSendTerm = (hub_uuid: string) => {
    navigate(`/${hub_uuid}/termos`)
  }

  useEffect(() => {
    if (selectedHub) {
      hasTermPending()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHub])

  const navItems = data.navItems.map((item) => {
    return {
      ...item,
      isActive: item.url === location.pathname,
    }
  })

  const handleChangeHub = (selectedOption: IOptions) => {
    setSelectedHub(selectedOption.value)
  }

  const listOrderHubs = () => {
    if (!user) return []
    return user.hubs
      .map((hub) => ({
        value: hub.uuid,
        label: hub.trading_name || hub.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  const useShortcut = (callback: () => void) => {
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowDown') {
          event.preventDefault()
          callback()
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [callback])
  }

  useShortcut(() => {
    console.log('Ctrl + Seta para Baixo! - Teste Notificação de Pedido')
    toggleNotificationTest(true)
  })

  return (
    <>
      {showStatusBar && (
        <StatusBar
          status={colorStatusBar}
          text={messageStatusBar}
          actions={
            selectedHub?.pivot?.user_role.name === 'owner'
              ? [
                  {
                    text: 'Leia e aceite o termos de adesão',
                    onClick: () => onSendTerm(selectedHub?.uuid ?? ''),
                  },
                ]
              : null
          }
        />
      )}
      <div className='relative h-full w-full md:flex flex-nowrap bg-white bg-opacity-10'>
        <header className='md:hidden sticky top-0 z-20 bg-primary-900 items-center flex justify-between py-2 px-3'>
          <Button
            color='primary'
            onClick={() => toggleNavSideBar(true)}
            dataTestId='button-menu-mobile'>
            <IconNavigationMenu className='h-5 w-5' />
          </Button>
          <Button
            className='relative'
            onClick={() => toggleNotificationSidebar(true)}
            dataTestId='button-notification-mobile'>
            <IconReceiptSlip className='h-5 w-5' />
          </Button>
          {ordersProposal && ordersProposal.data.length > 0 && (
            <div className='absolute top-0 right-0'>
              <Badge
                status='primary'
                text={ordersProposal?.data.length ?? 0}
                variant='default'
                size='small'
                dataTestId='badge-notification-mobile'
              />
            </div>
          )}
        </header>

        {isMobile ? (
          <Drawer open={showNavSideBar} onClose={() => toggleNavSideBar(false)} direction='left'>
            <Sidebar
              dataTestId='layout-sidebar'
              navItems={navItems}
              logout={logout}
              selectedCompany={
                selectedHub
                  ? { value: selectedHub.uuid, label: selectedHub.trading_name }
                  : { value: '', label: '' }
              }
              companiesOptions={listOrderHubs()}
              onChangeCompany={(selectedOption: IOptions) => handleChangeHub(selectedOption)}
              userCurrentHub={`${user?.name} ${user?.surname}`}
            />
          </Drawer>
        ) : (
          <Sidebar
            dataTestId='layout-sidebar'
            navItems={navItems}
            logout={logout}
            selectedCompany={
              selectedHub
                ? { value: selectedHub.uuid, label: selectedHub.trading_name }
                : { value: '', label: '' }
            }
            companiesOptions={listOrderHubs()}
            onChangeCompany={(selectedOption: IOptions) => handleChangeHub(selectedOption)}
            userCurrentHub={`${user?.name} ${user?.surname}`}
            showHelp
          />
        )}
        <div className='flex bg-gray-50 w-full h-screen'>
          <div className='flex-1 overflow-auto relative'>
            <Outlet />
          </div>
        </div>
        {isMobile ? (
          <Drawer
            open={showNotificationSidebar}
            onClose={() => toggleNotificationSidebar(false)}
            direction='right'>
            <NotificationSidebar />
          </Drawer>
        ) : (
          <NotificationSidebar />
        )}
      </div>
      {showNotificationTest && (
        <div className='fixed bottom-4 right-4 z-50'>
          <NotificationTest />
        </div>
      )}
    </>
  )
}

export default DrawerLayout
